import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, POINT_SPRITE_GLSL, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Video displacement field. A flat grid of points facing the camera samples the
 * selected video (`uMedia`) at each point's UV and pushes the point along the
 * surface normal (+Z) by the sampled luminance (Rec.709), centered on a
 * configurable threshold: bright → toward the camera, dark → away. The
 * displacement magnitude is modulated in real time by the (already smoothed)
 * audio. The shader logic is fully decoupled from the audio analysis, which
 * lives in the single AudioEngine → updateUniforms pipeline.
 */
export class VideoDisplaceField extends BasePrimitive {
  object3D: THREE.Points
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute vec2 aUv;
        // Two possible sources: the primitive's own Texture (uPrimitiveMedia, like
        // the LED panel) — preferred — or the background video (uMedia) as a fallback.
        uniform sampler2D uMedia;
        uniform sampler2D uPrimitiveMedia;
        uniform float uMediaActive, uMediaAspect, uPrimitiveMediaActive, uPrimitiveAspect;
        uniform float uDispScale, uDispThreshold, uDispAudio, uDispBand;
        uniform float uBass, uMid, uTreble, uLevel, uBeat, uReactivity;
        uniform float uPixelRatio, uPointScale;
        varying vec3 vColor;
        varying float vActive;
        varying vec3 vDir;   // surface direction for the zone shapes
        ${ZONE_GLSL_COMMON}
        float pickBand() {
          if (uDispBand < 0.5) return uLevel;
          if (uDispBand < 1.5) return uBass;
          if (uDispBand < 2.5) return uMid;
          return uTreble;
        }
        void main() {
          float usePrim = step(0.5, uPrimitiveMediaActive);
          float hasMedia = max(usePrim, step(0.5, uMediaActive));
          // Only sample the video when one is actually loaded. Sampling the 1x1
          // dummy in the vertex shader can return NaN on some drivers, which would
          // give every point a NaN position (the GPU then draws nothing).
          vec3 c = vec3(0.0);
          float disp = 0.0;
          if (hasMedia > 0.5) {
            float aspect = usePrim > 0.5 ? uPrimitiveAspect : uMediaAspect;
            // Cover-fit the (square-grid) UV to the video aspect so it isn't stretched.
            // NOTE: named sampleUv (not uv): three.js injects an attribute vec2 uv
            // into every ShaderMaterial, so a local named uv collides (compile error).
            vec2 sampleUv = aUv;
            if (aspect > 1.0) sampleUv.x = (aUv.x - 0.5) / aspect + 0.5;
            else sampleUv.y = (aUv.y - 0.5) * aspect + 0.5;
            c = usePrim > 0.5
              ? texture2D(uPrimitiveMedia, sampleUv).rgb
              : texture2D(uMedia, sampleUv).rgb;
            float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));   // Rec.709 luminance
            // Audio gain: ~1 at rest, lifts with the chosen band + a beat punch
            // (all smoothed upstream). Frozen to 1 when Zone reaction is on, so the
            // static video relief stays and only the zones react (like the others).
            float gain = mix(1.0 + uDispAudio * pickBand() * 2.0 + uBeat * 0.5 * uReactivity,
                             1.0, uZoneEnabled);
            disp = (lum - uDispThreshold) * uDispScale * gain * uReactivity;
          }
          // Zone reaction: shapes ride the grid (mapped from UV) and push the points
          // along the +Z normal on their band's onset. zoneDisp is 0 when zones are off.
          vec3 zdir = zoneDirFromUV(aUv);
          vDir = zdir;
          float zone = zoneDisp(zdir) * 1.6;
          vColor = c;
          vActive = hasMedia;
          vec3 pos = position + vec3(0.0, 0.0, disp + zone);  // displace along the +Z normal
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          // Clamp the FINAL size (after pixelRatio + the user's Point size) so a low
          // Point size can't drop it below ~1px and make the points vanish.
          float px = 6.0 * (6.0 / -mv.z) * uPixelRatio * uPointScale;
          gl_PointSize = clamp(px, 2.0, 48.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uHue, uLevel;
        varying vec3 vColor;
        varying float vActive;
        varying vec3 vDir;
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          float core = pointMask();
          if (core < 0.01) discard;
          // No video yet → a bright grid so you can see the field is alive while
          // you pick a clip. With a video → its projected color.
          vec3 col = vActive > 0.5
            ? vColor * (0.85 + 0.5 * uLevel)
            : hsv2rgb(vec3(uHue, 0.45, 0.85));
          // Zone reaction: points inside a band's shape take its color and glow.
          vec4 z = zoneColor(vDir);
          col = mix(col, z.rgb, z.a);
          float alpha = uPointSolid > 0.5 ? core : core * 0.85;
          alpha = max(alpha, z.a * core);
          gl_FragColor = vec4(col, alpha);
        }
      `,
    })
    this.object3D = new THREE.Points(new THREE.BufferGeometry(), this.material)
    // Lean the grid back a touch so the +Z relief reads as 3D even with a static,
    // head-on camera (otherwise the displacement points straight at the viewer).
    this.object3D.rotation.x = -0.5
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    // Solid points occlude (normal blending + depth); soft points glow (additive).
    const solid = params.pointSolid === true
    this.material.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.material.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    // Square grid facing the camera; density scales the resolution.
    const N = Math.max(16, Math.round(200 * Math.sqrt(params.density ?? 1)))
    const half = 2.6 // grid spans [-2.6, 2.6] so the whole video frames in view
    const count = N * N
    const positions = new Float32Array(count * 3)
    const uvs = new Float32Array(count * 2)
    let i = 0
    for (let yy = 0; yy < N; yy++) {
      for (let xx = 0; xx < N; xx++) {
        const u = xx / (N - 1)
        const v = yy / (N - 1)
        positions[i * 3 + 0] = (u - 0.5) * 2.0 * half
        positions[i * 3 + 1] = (v - 0.5) * 2.0 * half
        positions[i * 3 + 2] = 0
        uvs[i * 2 + 0] = u
        uvs[i * 2 + 1] = v
        i++
      }
    }
    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aUv', new THREE.BufferAttribute(uvs, 2))
    geo.computeBoundingSphere()
  }
}
